import type { InstallmentItem } from '@/core/api/models/unitInstallments';
import { useTranslation } from '@/core/hooks';
import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { PaymentFeesResponse, paymentService } from '../services/paymentService';
import { formatDueDateWithPrefix } from '../utils/installmentUtils';

/**
 * Installment item with property information
 */
type InstallmentItemWithProperty = InstallmentItem & {
    propertyId: string;
    propertyName: string;
    isOnlinePaymentEnabled: boolean;
};

/**
 * Selected payment data structure
 */
export interface SelectedPaymentData {
    id: string;
    mid?: string | null;
    propertyName: string;
    title: string;
    amount: number; // due_amount for display
    dueDate: string; // Formatted as "Due by DD/MM/YYYY"
    payment_type: 'installment' | 'other_charge';
    originalItem: any; // Store original item for checkout
}

/**
 * Hook for payment summary screen
 * Fetches fees and transforms selected payment IDs into display data
 */
export function usePaymentSummary(selectedPaymentIds: string[]) {
    const { t } = useTranslation();
    const properties = useAppStore((state) => state.properties);
    const unitInstallments = useAppStore((state) => state.unitInstallments);
    const isAdhaJourney = useAppStore((state) => state.isAdhaJourney);

    // Fetch payment fees
    const { data: feesData, isLoading: isLoadingFees, error: feesError } = useQuery<PaymentFeesResponse>({
        queryKey: ['paymentFees'],
        queryFn: () => paymentService.getPaymentFees(),
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });

    // Transform selected payment IDs into display data
    const selectedPayments = useMemo<SelectedPaymentData[]>(() => {
        if (selectedPaymentIds.length === 0) {
            return [];
        }

        // Create a Set for O(1) lookup of selected payment IDs
        const selectedIdsSet = new Set(selectedPaymentIds);
        
        // Single loop through all properties to collect all payment types (overdue + due soon + future)
        // Use Map for O(1) lookup by payment ID
        const paymentsMap = new Map<string, InstallmentItemWithProperty>();
        const propertyNameCache = new Map<string, string>(); // Cache property names
        
        properties.forEach((property) => {
            const installments = unitInstallments[property.id];
            if (!installments) return;
            // Same profile unit used for the payment name. ADHA checkout is unchanged.
            if (!isAdhaJourney && property.isOnlinePaymentEnabled !== true) return;
            
            // Cache property name to avoid repeated lookups
            const propertyName = property.unitNumber || property.projectName || 'Unknown Property';
            propertyNameCache.set(property.id, propertyName);
            
            // Process all payment types in one loop
            const paymentArrays = [
                { items: installments.overdue || [], category: 'overdue' },
                { items: installments.upcomingDue || [], category: 'upcomingDue' },
                { items: installments.future || [], category: 'future' },
            ];
            
            paymentArrays.forEach(({ items }) => {
                items.forEach((item) => {
                    // Only add to map if it's in the selected IDs set
                    if (selectedIdsSet.has(item.id)) {
                        paymentsMap.set(item.id, {
                            ...item,
                            propertyId: property.id,
                            propertyName,
                            isOnlinePaymentEnabled: property.isOnlinePaymentEnabled === true,
                        });
                    }
                });
            });
        });

        // Transform payments from map to display data (only for selected IDs)
        const payments: SelectedPaymentData[] = [];
        
        selectedPaymentIds.forEach((paymentId) => {
            const payment = paymentsMap.get(paymentId);
            if (!payment) {
                logger.warn('usePaymentSummary:Payment not found', { paymentId });
                return;
            }
            
            // Translate payment type
            const typeLower = payment.type?.toLowerCase() || '';
            let translatedType = payment.type || '';
            if (typeLower.includes('handover')) {
                translatedType = t('propertyDetails.handover');
            } else if (typeLower.includes('installment')) {
                translatedType = t('propertyDetails.installment');
            } else if (typeLower.includes('down payment')) {
                translatedType = t('propertyDetails.downPayment');
            } else if (typeLower.includes('adm fee') || typeLower.includes('adm')) {
                translatedType = t('propertyDetails.admFee');
            }

            payments.push({
                id: payment.id,
                mid: payment.mid || null,
                propertyName: payment.propertyName,
                title: translatedType,
                amount: payment.due_amount || 0, // Use due_amount for payments
                dueDate: formatDueDateWithPrefix(payment.due_date, t('payments.dueBy')),
                payment_type: payment.payment_type || 'installment',
                originalItem: payment,
            });
        });

        return payments;
    }, [selectedPaymentIds, unitInstallments, properties, t, isAdhaJourney]);

    // Separate installments and charges
    const installments = useMemo(() => {
        return selectedPayments.filter((p) => p.payment_type === 'installment');
    }, [selectedPayments]);

    const charges = useMemo(() => {
        return selectedPayments.filter((p) => p.payment_type === 'other_charge');
    }, [selectedPayments]);

    // Calculate totals
    const subtotal = useMemo(() => {
        return selectedPayments.reduce((total, payment) => total + payment.amount, 0);
    }, [selectedPayments]);

    // Calculate late payment fee (only for overdue payments)
    const latePaymentFee = useMemo(() => {
        // TODO: This should come from API or be calculated based on overdue days
        // For now, return 0 as the API doesn't provide this
        return 0;
    }, [selectedPayments]);

    // Calculate convenience fee on total (installments + charges)
    // Formula: Total of all payments (installments + charges) * convenienceFee percentage
    const convenienceFee = useMemo(() => {
        if (!feesData || feesData.convenienceFee === undefined || feesData.convenienceFee === null) {
            return 0;
        }
        // feesData.convenienceFee is a decimal (e.g., 0.01 for 1%)
        // Calculate: subtotal * convenienceFee
        const calculated = subtotal * feesData.convenienceFee;
        // Round to 2 decimal places
        return Math.round(calculated * 100) / 100;
    }, [subtotal, feesData]);

    // Calculate total before card fees
    const totalBeforeCardFee = useMemo(() => {
        return subtotal + latePaymentFee + convenienceFee;
    }, [subtotal, latePaymentFee, convenienceFee]);

    // Calculate domestic card fee
    const domesticCardFee = useMemo(() => {
        if (!feesData?.domesticRate) return 0;
        return Math.round(totalBeforeCardFee * feesData.domesticRate * 100) / 100;
    }, [totalBeforeCardFee, feesData?.domesticRate]);

    // Calculate international card fee
    const internationalCardFee = useMemo(() => {
        if (!feesData?.internationalRate) return 0;
        return Math.round(totalBeforeCardFee * feesData.internationalRate * 100) / 100;
    }, [totalBeforeCardFee, feesData?.internationalRate]);

    return {
        selectedPayments,
        installments,
        charges,
        feesData,
        isLoadingFees,
        feesError,
        subtotal,
        latePaymentFee,
        convenienceFee,
        totalBeforeCardFee,
        domesticCardFee,
        internationalCardFee,
    };
}

